import { useEffect, useState } from "react";
import {
  useCronHealth,
  useRuns,
  useRunDetail,
  type CronHealth,
  type FailureStreak,
  type RunSummary,
  type RunCompany,
} from "../lib/runs";
import { Button } from "../components/ui/Button";
import { Pill, type PillTone } from "../components/ui/Pill";
import { EmptyState } from "../components/ui/EmptyState";
import { Card } from "../components/ui/Card";
import {
  TableShell,
  Table as UITable,
  THead,
  TRow,
  TCell,
} from "../components/ui/Table";
import { Skeleton, SkeletonRow } from "../components/ui/Skeleton";
import { activateOnKey } from "../lib/keyboard";

export default function Runs() {
  return (
    <section className="p-4 sm:p-6 lg:p-8">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Runs</h1>
        <p className="mt-1 text-sm text-neutral-400">
          Cron-driven catalog refreshes.
        </p>
      </header>

      <CronTab />
    </section>
  );
}

// ── Cron tab (preserved from prior single-column layout) ──────────────────

function CronTab() {
  const list = useRuns();
  const cron = useCronHealth();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedId) return;
    const first = list.data?.runs[0]?.id;
    if (first) setSelectedId(first);
  }, [list.data, selectedId]);

  return (
    <div className="grid grid-cols-1 gap-6 pt-4 lg:grid-cols-[1fr_24rem]">
      <div className="min-w-0">
        <header className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold tracking-tight">Check runs</h2>
          <span className="text-xs text-neutral-500" aria-live="polite">
            {list.data ? (
              `${list.data.runs.length} recent`
            ) : list.isLoading ? (
              <Skeleton className="inline-block h-3 w-20" />
            ) : null}
          </span>
        </header>
        <p className="mt-1 text-sm text-neutral-400">
          Cron fan-outs every 6 hours plus any &quot;Run now&quot; triggers from
          the Companies page. In-flight runs poll automatically.
        </p>

        <CronHealthBlock health={cron.data} loading={cron.isLoading} />

        <div className="mt-6">
          {list.isLoading ? (
            <TableShell>
              <UITable>
                <THead>
                  <RunsHeaderRow />
                </THead>
                <tbody>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <SkeletonRow key={i} cols={7} />
                  ))}
                </tbody>
              </UITable>
            </TableShell>
          ) : list.error ? (
            <TableShell>
              <EmptyState
                tone="danger"
                title="Couldn't load runs"
                description={String(list.error)}
                action={
                  <Button variant="secondary" onClick={() => list.refetch()}>
                    Retry
                  </Button>
                }
              />
            </TableShell>
          ) : list.data!.runs.length === 0 ? (
            <TableShell>
              <EmptyState
                title="No check runs yet."
                description="Wait for the next cron tick or trigger one from /companies."
                action={
                  <Button
                    variant="secondary"
                    onClick={() => {
                      window.location.href = "/companies";
                    }}
                  >
                    Go to companies
                  </Button>
                }
              />
            </TableShell>
          ) : (
            <TableShell>
              <UITable>
                <THead>
                  <RunsHeaderRow />
                </THead>
                <tbody>
                  {list.data!.runs.map((r) => (
                    <RunRow
                      key={r.id}
                      run={r}
                      selected={selectedId === r.id}
                      onSelect={() => setSelectedId(r.id)}
                    />
                  ))}
                </tbody>
              </UITable>
            </TableShell>
          )}
        </div>
      </div>

      <RunDetailPanel runId={selectedId} />
    </div>
  );
}

function RunsHeaderRow() {
  return (
    <tr>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Started
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Scope
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Status
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Progress
      </th>
      <th scope="col" className="px-4 py-3 text-right font-medium">
        +new
      </th>
      <th scope="col" className="px-4 py-3 text-right font-medium">
        −rem
      </th>
      <th scope="col" className="px-4 py-3 text-right font-medium">
        ~chg
      </th>
    </tr>
  );
}
function RunRow({
  run,
  selected,
  onSelect,
}: {
  run: RunSummary;
  selected: boolean;
  onSelect: () => void;
}) {
  const inFlight = run.queued_count + run.running_count > 0;
  return (
    <TRow
      tabIndex={0}
      role="button"
      onClick={onSelect}
      onKeyDown={(e) => activateOnKey(e, onSelect)}
      className={`cursor-pointer hover:bg-neutral-900/60 ${
        selected ? "bg-neutral-900/80" : ""
      }`}
      aria-selected={selected}
    >
      <TCell className="text-neutral-400 whitespace-nowrap">
        {new Date(run.started_at).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </TCell>
      <TCell className="font-mono text-xs text-neutral-400">{run.scope}</TCell>
      <TCell>
        <StatusPill status={inFlight ? "running" : run.status} />
      </TCell>
      <TCell>
        <ProgressBar
          done={run.done_count}
          total={run.company_count}
          errors={run.error_count}
        />
      </TCell>
      <TCell className="text-right font-mono text-success-fg">
        {run.totals.new_count || ""}
      </TCell>
      <TCell className="text-right font-mono text-danger-fg">
        {run.totals.removed_count || ""}
      </TCell>
      <TCell className="text-right font-mono text-warning-fg">
        {run.totals.changed_count || ""}
      </TCell>
    </TRow>
  );
}

function RunDetailPanel({ runId }: { runId: string | null }) {
  const detail = useRunDetail(runId);

  if (!runId) {
    return (
      <Card className="p-6 self-start lg:sticky lg:top-4 text-sm text-neutral-500">
        Select a run to see per-company status.
      </Card>
    );
  }

  return (
    <Card className="p-5 self-start lg:sticky lg:top-4 text-sm">
      {detail.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-3/4" />
        </div>
      ) : detail.error ? (
        <EmptyState
          tone="danger"
          title="Couldn't load run"
          description={String(detail.error)}
          action={
            <Button variant="secondary" onClick={() => detail.refetch()}>
              Retry
            </Button>
          }
        />
      ) : !detail.data ? (
        <EmptyState title="Run not found." />
      ) : (
        <>
          <header className="border-b border-neutral-800 pb-4">
            <p className="text-xs uppercase tracking-wider text-neutral-500">
              Run · {detail.data.scope}
            </p>
            <h2 className="mt-1 inline-flex items-center gap-2 text-base font-semibold text-neutral-100">
              <StatusPill status={detail.data.status} />
              <span className="font-mono text-xs text-neutral-500">
                {detail.data.id.slice(0, 8)}
              </span>
            </h2>
            <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <Stat label="Companies" value={detail.data.company_count} />
              <Stat label="Done" value={detail.data.done_count} />
              <Stat label="Errors" value={detail.data.error_count} tone="error" />
              <Stat
                label="Running"
                value={detail.data.queued_count + detail.data.running_count}
              />
              <Stat label="+ new" value={detail.data.totals.new_count} tone="ok" />
              <Stat
                label="− removed"
                value={detail.data.totals.removed_count}
                tone="warn"
              />
              <Stat label="~ changed" value={detail.data.totals.changed_count} />
              <Stat
                label="Duration"
                value={
                  detail.data.finished_at
                    ? formatDuration(
                        detail.data.finished_at - detail.data.started_at,
                      )
                    : "…"
                }
              />
            </dl>
          </header>

          <div className="mt-4 max-h-[28rem] overflow-y-auto pr-1">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-neutral-500">
                <tr>
                  <th scope="col" className="py-2 text-left font-medium">
                    Company
                  </th>
                  <th scope="col" className="py-2 text-left font-medium">
                    Status
                  </th>
                  <th scope="col" className="py-2 text-right font-medium">
                    +/−/~
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.data.companies.map((c) => (
                  <CompanyDetailRow key={c.company_id} company={c} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

function CompanyDetailRow({ company }: { company: RunCompany }) {
  return (
    <tr className="border-t border-neutral-800/40 align-top">
      <td className="py-2 pr-2 text-neutral-200">
        {company.company_name ?? company.company_id}
        {company.tier ? (
          <span className="ml-1 text-[10px] text-neutral-500">T{company.tier}</span>
        ) : null}
      </td>
      <td className="py-2 pr-2">
        <StatusPill status={company.status} small />
        {company.error ? (
          <p className="mt-1 break-words text-[10px] text-danger-fg">
            {company.error}
          </p>
        ) : null}
      </td>
      <td className="py-2 text-right font-mono text-neutral-400">
        <span className="text-success-fg">
          {company.new_count ? `+${company.new_count}` : ""}
        </span>
        <span className="text-danger-fg">
          {company.removed_count ? ` −${company.removed_count}` : ""}
        </span>
        <span className="text-warning-fg">
          {company.changed_count ? ` ~${company.changed_count}` : ""}
        </span>
      </td>
    </tr>
  );
}

function StatusPill({ status, small }: { status: string; small?: boolean }) {
  const tone = STATUS_TONE[status] ?? "neutral";
  return (
    <Pill tone={tone} className={small ? "text-[10px] px-1.5" : undefined}>
      {status}
    </Pill>
  );
}

const STATUS_TONE: Record<string, PillTone> = {
  running: "info",
  queued: "neutral",
  done: "success",
  completed: "success",
  complete: "success",
  error: "danger",
  failed: "danger",
  pending: "neutral",
};

function ProgressBar({
  done,
  total,
  errors,
}: {
  done: number;
  total: number;
  errors: number;
}) {
  if (total === 0) return <span className="text-neutral-600">—</span>;
  const donePct = (done / total) * 100;
  const errorPct = (errors / total) * 100;
  return (
    <div
      className="flex items-center gap-2"
      role="progressbar"
      aria-valuenow={done + errors}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-label={`${done + errors} of ${total} done`}
    >
      <div className="h-1.5 w-32 overflow-hidden rounded bg-neutral-800">
        <div
          className="h-full bg-success-fg/70"
          style={{ width: `${donePct}%` }}
        />
        <div
          className="-mt-1.5 h-full bg-danger-fg/70"
          style={{ width: `${errorPct}%`, marginLeft: `${donePct}%` }}
        />
      </div>
      <span className="text-xs text-neutral-500">
        {done + errors}/{total}
      </span>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: "ok" | "warn" | "error";
}) {
  const toneCls =
    tone === "ok"
      ? "text-success-fg"
      : tone === "warn"
        ? "text-warning-fg"
        : tone === "error"
          ? "text-danger-fg"
          : "text-neutral-100";
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</dt>
      <dd className={`text-sm font-medium ${toneCls}`}>{value}</dd>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}m ${rs}s`;
}

function CronHealthBlock({
  health,
  loading,
}: {
  health: CronHealth | undefined;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="p-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-3 h-4 w-32" />
            <Skeleton className="mt-2 h-3 w-40" />
          </Card>
        ))}
      </div>
    );
  }
  if (!health) return null;

  const last = health.last_run;
  const streaks = health.failure_streaks;
  const ageMs = last ? Date.now() - last.started_at : null;
  const expectedNextAt = last
    ? last.started_at - (health.last_skew_ms ?? 0) + health.expected_interval_ms
    : null;
  const overdueMs = expectedNextAt !== null ? Date.now() - expectedNextAt : null;
  const overdue = overdueMs !== null && overdueMs > 30 * 60 * 1000;

  return (
    <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
      <Card className="p-4">
        <p className="text-[10px] uppercase tracking-wider text-neutral-500">
          Last cron tick
        </p>
        {last ? (
          <>
            <p className="mt-2 text-sm font-medium text-neutral-100">
              {new Date(last.started_at).toLocaleString()}
            </p>
            <p className="mt-1 text-xs text-neutral-500">
              {ageMs !== null ? `${formatAge(ageMs)} ago` : ""}
              {health.last_skew_ms !== null ? (
                <span className="ml-2 text-neutral-400">
                  · fired {formatDuration(health.last_skew_ms)} after schedule
                </span>
              ) : null}
            </p>
            <div className="mt-3 flex items-center gap-2 text-xs">
              <StatusPill status={last.status} />
              <span className="text-neutral-400">
                {last.done_count}/{last.company_count} done
                {last.error_count > 0 ? (
                  <span className="ml-2 text-danger-fg">
                    · {last.error_count} error{last.error_count === 1 ? "" : "s"}
                  </span>
                ) : null}
              </span>
            </div>
          </>
        ) : (
          <p className="mt-2 text-sm text-neutral-500">No cron runs yet.</p>
        )}
      </Card>

      <Card className="p-4">
        <p className="text-[10px] uppercase tracking-wider text-neutral-500">
          Schedule
        </p>
        <p className="mt-2 font-mono text-xs text-neutral-300">0 */6 * * * UTC</p>
        <p className="mt-1 text-xs text-neutral-500">
          00:00 / 06:00 / 12:00 / 18:00 UTC
        </p>
        {expectedNextAt ? (
          <p className="mt-3 inline-flex items-center gap-2 text-xs text-neutral-400">
            <span>
              Next expected: {new Date(expectedNextAt).toLocaleString()}
            </span>
            {overdue ? (
              <Pill tone="danger" className="text-[10px]">
                overdue {formatDuration(overdueMs!)}
              </Pill>
            ) : null}
          </p>
        ) : null}
      </Card>

      <Card className="p-4">
        <p className="text-[10px] uppercase tracking-wider text-neutral-500">
          Failure streaks
          {streaks.length > 0 ? (
            <span className="ml-1 text-neutral-600">
              · last {streaks[0]!.window_size} ticks
            </span>
          ) : null}
        </p>
        {streaks.length === 0 ? (
          <p className="mt-2 text-sm text-neutral-500">
            No companies erroring 2+ times in the recent window.
          </p>
        ) : (
          <ul className="mt-2 space-y-1.5 text-xs">
            {streaks.slice(0, 4).map((s) => (
              <FailureStreakRow key={s.company_id} streak={s} />
            ))}
            {streaks.length > 4 ? (
              <li className="text-[11px] text-neutral-500">
                +{streaks.length - 4} more
              </li>
            ) : null}
          </ul>
        )}
      </Card>
    </div>
  );
}

function FailureStreakRow({ streak }: { streak: FailureStreak }) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-neutral-200">
        {streak.company_name ?? streak.company_id}
      </span>
      <span className="font-mono text-danger-fg">
        {streak.error_count}/{streak.window_size}
      </span>
    </li>
  );
}

function formatAge(ms: number): string {
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}
